import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  Icon,
  SearchBar,
  Select,
  Toast,
  Tooltip,
  type Column,
} from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import { useUrlParams } from "@/hooks/useUrlParams";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { newAxisEditionOptions } from "@/api/surveyTaxonomy.service";
import {
  optOuts,
  type OptOut,
  type OptOutScope,
  type OptOutSortField,
  type OptOutSource,
  type ValidateImportResponse,
} from "@/api/optOuts.service";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import { submissionsService, type TaxonomyMode } from "@/api/submissions.service";
import { usePublications } from "@/hooks/usePublications";
import { formatDateShort } from "@/utils/dateFormatter";
import { AddOptOutModal } from "./AddOptOutModal";
import { ImportPreviewModal } from "./ImportPreviewModal";
import {
  buildLegacyPublicationLookup,
  buildNewAxisEditionLookup,
  buildPublicationYearOptions,
  formatLegacyPublicationOption,
  hasStoredEditionUuid,
  isStoredEditionUuid,
  lowercaseEditionOptions,
  resolveStoredPublicationLabel,
} from "./optOutPublications.utils";
import styles from "./OptOuts.module.css";

const ITEMS_PER_PAGE = 50;

const SORTABLE_COLUMN_KEYS = [
  "email",
  "first_name",
  "last_name",
  "firm_ref",
  "edition",
  "scope",
  "created_at",
];

const SCOPE_LABEL: Record<OptOutScope, string> = {
  firm: "Firm",
  edition: "Publication",
  global: "Global",
};

const SCOPE_COLOR: Record<OptOutScope, "primary" | "secondary" | "warning"> = {
  firm: "primary",
  edition: "secondary",
  global: "warning",
};

// Provenance of an opt-out row. The three referee-originated sources share one
// colour so staff can tell "the referee did this" from "we did this" at a glance.
// `legacy_import` is the historic opt-out list we migrated in: the original actor
// was never recorded, so it is neither Staff nor Referee — do not relabel it.
const SOURCE_LABEL: Record<OptOutSource, string> = {
  staff: "Staff",
  preference_centre: "Referee (preference centre)",
  sendgrid_unsubscribe: "Referee (email unsubscribe)",
  spamreport: "Referee (spam report)",
  legacy_import: "Legacy import",
};

const SOURCE_COLOR: Record<OptOutSource, "primary" | "secondary" | "default"> = {
  staff: "primary",
  preference_centre: "secondary",
  sendgrid_unsubscribe: "secondary",
  spamreport: "secondary",
  legacy_import: "default",
};

const SCOPE_FILTER_OPTIONS = [
  { value: "", label: "All scopes" },
  { value: "firm", label: "Firm-level" },
  { value: "edition", label: "Publication-wide" },
  { value: "global", label: "Global (all emails)" },
];

function OptOutsPage() {
  const currentYear = new Date().getFullYear();
  const { searchParams, updateUrl } = useUrlParams();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("staff.campaign.write");
  const { toggleVisible, mode } = useTaxonomyMode();

  const [searchTerm, setSearchTerm] = useState(searchParams.get("email") || "");
  const [publicationYearFilter, setPublicationYearFilter] = useState("");
  const [publicationFilter, setPublicationFilter] = useState(searchParams.get("edition") || "");
  const [firmFilter, setFirmFilter] = useState(searchParams.get("firm_ref") || "");
  const [scopeFilter, setScopeFilter] = useState(searchParams.get("scope") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [sortField, setSortField] = useState<OptOutSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [reloadKey, setReloadKey] = useState(0);

  const [data, setData] = useState<{ optOuts: OptOut[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [firmNames, setFirmNames] = useState<Record<string, string>>({});

  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<OptOut | null>(null);
  const [pendingDelete, setPendingDelete] = useState<OptOut | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [importPreview, setImportPreview] = useState<{
    csv: string;
    preview: ValidateImportResponse;
  } | null>(null);
  const [validatingImport, setValidatingImport] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch the opt-out list.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    optOuts
      .list({
        email: searchTerm || undefined,
        edition: publicationFilter || undefined,
        firmRef: firmFilter || undefined,
        scope: (scopeFilter as OptOutScope) || undefined,
        sortField: sortField || undefined,
        sortDirection: sortField ? sortDirection : undefined,
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      })
      .then(resp => {
        if (cancelled) return;
        setData({ optOuts: resp.opt_outs, total: resp.pagination.total });
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    searchTerm,
    publicationFilter,
    firmFilter,
    scopeFilter,
    currentPage,
    sortField,
    sortDirection,
    reloadKey,
  ]);

  // Build a Firm Ref -> firm name lookup once on mount, used ONLY to label the
  // Firm filter dropdown (value = firm_ref, label = name). The authoritative
  // firm name shown in the table now comes from the API (`row.firm_name`), so
  // this lookup no longer drives the column display. firm_ref on opt-outs equals
  // submissions.firm_ref, so the submissions "uniques" endpoint (no edition
  // filter = all distinct firms) gives us names for the full firm list.
  // Best-effort: on failure we leave the map empty and the filter shows no
  // firm-specific options.
  useEffect(() => {
    let cancelled = false;
    submissionsService
      .getUniques("firm")
      .then(resp => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const value of resp.values) {
          map[String(value.id)] = value.name;
        }
        setFirmNames(map);
      })
      .catch(() => {
        // Best-effort: leave the map empty so the column shows the raw ref.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: allPublicationsData } = usePublications({});
  const { data: yearPublicationsData } = usePublications(
    { edition: publicationYearFilter || undefined },
    { enabled: mode === "legacy" && !!publicationYearFilter },
  );

  const publicationLookup = useMemo(
    () => buildLegacyPublicationLookup(allPublicationsData?.publications ?? []),
    [allPublicationsData?.publications],
  );

  const rows = useMemo(() => data?.optOuts ?? [], [data?.optOuts]);
  const total = data?.total ?? 0;

  const rowsHaveEditionUuid = useMemo(
    () => hasStoredEditionUuid(rows.map(row => row.edition)),
    [rows],
  );

  const { data: newAxisEditions } = useNewAxisEditions({
    enabled: toggleVisible && (mode === "new" || rowsHaveEditionUuid),
  });

  const newAxisEditionLookup = useMemo(
    () => buildNewAxisEditionLookup(newAxisEditions),
    [newAxisEditions],
  );

  const publicationYearFilterOptions = useMemo(
    () => [{ value: "", label: "All years" }, ...buildPublicationYearOptions(currentYear).slice(1)],
    [currentYear],
  );

  const publicationFilterOptions = useMemo(() => {
    if (mode === "new") {
      return [
        { value: "", label: "All publications" },
        ...lowercaseEditionOptions(newAxisEditionOptions(newAxisEditions, publicationYearFilter)),
      ];
    }
    const pubs = publicationYearFilter
      ? (yearPublicationsData?.publications ?? [])
      : (allPublicationsData?.publications ?? []);
    return [
      { value: "", label: "All publications" },
      ...pubs.map((p: LegacyPublication) => formatLegacyPublicationOption(p)),
    ];
  }, [
    mode,
    newAxisEditions,
    publicationYearFilter,
    yearPublicationsData?.publications,
    allPublicationsData?.publications,
  ]);

  // Firm filter options (value = firm_ref, label = name), sorted by name. Labels
  // come from the submissions "uniques" lookup — this is filter-labels-only; the
  // authoritative displayed firm name in the table comes from the API.
  const firmFilterOptions = useMemo(() => {
    const options = Object.entries(firmNames)
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: "", label: "All firms" }, ...options];
  }, [firmNames]);

  const columns: Column<OptOut>[] = [
    { header: "Email", accessor: "email" },
    {
      header: "First name",
      accessor: "first_name",
      render: (value: unknown) => (value ? String(value) : "—"),
    },
    {
      header: "Last name",
      accessor: "last_name",
      render: (value: unknown) => (value ? String(value) : "—"),
    },
    {
      header: "Firm",
      accessor: "firm_ref",
      // `firm_name` is resolved server-side (campaign-api, from mailing_group_member)
      // and is authoritative — display it directly. Fall back to the raw firm_ref
      // when the API couldn't resolve a name, then to a dash.
      render: (_value: unknown, row: OptOut) => row.firm_name ?? row.firm_ref ?? "—",
    },
    {
      header: "Publication",
      accessor: "edition",
      // The stored `edition` is the legacy_publication.id. Show the compact id
      // inline and reveal the full "publication / edition" label in a tooltip
      // (the full label is too long to show in the column). Global-scope rows
      // have no publication (null id) and render a dash.
      render: (value: unknown) => {
        if (!value) return "—";
        const id = String(value);
        if (isStoredEditionUuid(id)) {
          return resolveStoredPublicationLabel(id, publicationLookup, newAxisEditionLookup);
        }
        const fullLabel = resolveStoredPublicationLabel(id, publicationLookup);
        // Only surface the info icon+tooltip when we resolved a real name;
        // on a lookup miss `fullLabel === id`, so just show the id text.
        if (fullLabel === id) return id;
        return (
          <span className={styles.publicationCell}>
            {id}
            <Tooltip title="Publication" content={fullLabel} position="top">
              <span className={styles.publicationInfo} aria-label={fullLabel}>
                <Icon icon="info" size="small" />
              </span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      header: "Scope",
      accessor: "scope",
      render: (value: unknown) => {
        const scope = value as OptOutScope;
        return (
          <Chip size="small" variant="filled" color={SCOPE_COLOR[scope] ?? "default"}>
            {SCOPE_LABEL[scope] ?? String(value)}
          </Chip>
        );
      },
    },
    {
      header: "Added by",
      accessor: "source",
      render: (value: unknown) => {
        const source = value as OptOutSource;
        return (
          <Chip size="small" variant="filled" color={SOURCE_COLOR[source] ?? "default"}>
            {SOURCE_LABEL[source] ?? String(value)}
          </Chip>
        );
      },
    },
    {
      header: "Added",
      accessor: "created_at",
      render: (value: unknown) => {
        if (!value) return "—";
        try {
          return formatDateShort(String(value));
        } catch {
          return "—";
        }
      },
    },
    ...(canWrite
      ? [
          {
            header: "",
            accessor: "id" as keyof OptOut,
            render: (_value: unknown, row: OptOut) => (
              <div className={styles.actionsCell}>
                <Button
                  variant="outlined"
                  size="small"
                  aria-label={`Edit opt-out for ${row.email}`}
                  onClick={() => setPendingEdit(row)}
                >
                  <Icon icon="edit" size="small" />
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  aria-label={`Delete opt-out for ${row.email}`}
                  onClick={() => setPendingDelete(row)}
                >
                  <Icon icon="delete" size="small" />
                </Button>
              </div>
            ),
          } as Column<OptOut>,
        ]
      : []),
  ];

  const handleSort = useCallback(
    (field: string) => {
      const nextField = field as OptOutSortField;
      if (nextField !== sortField) {
        setSortField(nextField);
        setSortDirection("asc");
      } else if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortField(null);
        setSortDirection("asc");
      }
      setCurrentPage(1);
    },
    [sortField, sortDirection],
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    updateUrl({ email: value, page: "1" });
  };

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    setPublicationFilter("");
    setCurrentPage(1);
    updateUrl({ [TAXONOMY_URL_PARAM]: next, edition: "", page: "1" });
  };

  const handlePublicationYearFilterChange = (value: string) => {
    setPublicationYearFilter(value);
    setPublicationFilter("");
    setCurrentPage(1);
    updateUrl({ edition: "", page: "1" });
  };

  const handlePublicationFilterChange = (value: string) => {
    setPublicationFilter(value);
    setCurrentPage(1);
    updateUrl({ edition: value, page: "1" });
  };

  const handleFirmFilterChange = (value: string) => {
    setFirmFilter(value);
    setCurrentPage(1);
    updateUrl({ firm_ref: value, page: "1" });
  };

  const handleScopeFilterChange = (value: string) => {
    setScopeFilter(value);
    setCurrentPage(1);
    updateUrl({ scope: value, page: "1" });
  };

  const triggerReload = () => setReloadKey(k => k + 1);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    await optOuts.remove(pendingDelete.id);
    setPendingDelete(null);
    triggerReload();
  };

  const handleDownload = async () => {
    try {
      await optOuts.exportCsv();
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : "Failed to download CSV",
        type: "error",
      });
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Step 1: validate (dry-run) the selected file, then open the preview modal.
  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again re-triggers change.
    event.target.value = "";
    if (!file) return;
    setValidatingImport(true);
    try {
      const csv = await file.text();
      const preview = await optOuts.validateImportCsv(csv);
      setImportPreview({ csv, preview });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : "Failed to validate CSV",
        type: "error",
      });
    } finally {
      setValidatingImport(false);
    }
  };

  const errStatus = (error as (Error & { status?: number }) | null)?.status;
  if (errStatus === 401 || errStatus === 403) {
    return <UnauthorizedView />;
  }

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + rows.length;

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Opt-out management" subtitle="Referee Management">
        <PermissionGate permission="staff.campaign.write">
          <div className={styles.headerActions}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className={styles.hiddenFileInput}
              onChange={handleFileSelected}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleUploadClick}
              disabled={validatingImport}
            >
              {validatingImport ? "Validating…" : "Upload CSV"}
            </Button>
            <Button variant="contained" color="primary" onClick={handleDownload}>
              Download CSV
            </Button>
            <Button variant="contained" color="primary" onClick={() => setShowAddModal(true)}>
              Add entry
            </Button>
          </div>
        </PermissionGate>
      </PageHeader>

      {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}

      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          onClear={() => handleSearchChange("")}
          placeholder="Search by email…"
        />
      </div>

      <div className={styles.filterRow}>
        <div className={styles.filters}>
          <div className={styles.filterField}>
            <Select
              label="Year"
              value={publicationYearFilter}
              onChange={e => handlePublicationYearFilterChange(e.target.value)}
              options={publicationYearFilterOptions}
              size="small"
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Publication"
              value={publicationFilter}
              onChange={e => handlePublicationFilterChange(e.target.value)}
              options={publicationFilterOptions}
              size="small"
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Firm"
              value={firmFilter}
              onChange={e => handleFirmFilterChange(e.target.value)}
              options={firmFilterOptions}
              size="small"
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Scope"
              value={scopeFilter}
              onChange={e => handleScopeFilterChange(e.target.value)}
              options={SCOPE_FILTER_OPTIONS}
              size="small"
            />
          </div>
        </div>
      </div>

      {loading && <LoadingState message="Loading opt-outs…" />}

      {error && !loading && errStatus !== 401 && errStatus !== 403 && (
        <EmptyState variant="error" title="Failed to load opt-outs" message={error.message} />
      )}

      {!loading && !error && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            size="small"
            rowKey="id"
            emptyMessage="No opt-outs found."
            sortableColumnKeys={SORTABLE_COLUMN_KEYS}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={total}
            itemName="opt-outs"
            onPageChange={page => {
              setCurrentPage(page);
              updateUrl({ page: String(page) });
            }}
          />
        </>
      )}

      {showAddModal && (
        <AddOptOutModal
          onClose={() => setShowAddModal(false)}
          onSubmitted={({ skipReason }) => {
            setShowAddModal(false);
            if (skipReason === "duplicate") {
              setToast({
                message: "This opt-out already exists — nothing to add.",
                type: "info",
              });
            } else if (skipReason === "covered_by_broader") {
              setToast({
                message: "Already covered by a broader opt-out (publication / global) — not added.",
                type: "info",
              });
            } else {
              setToast({ message: "Opt-out added.", type: "success" });
            }
            // Reload either way: a collapse may have removed lower-scope rows in the DB.
            triggerReload();
          }}
        />
      )}

      {pendingEdit && (
        <AddOptOutModal
          entry={pendingEdit}
          onClose={() => setPendingEdit(null)}
          onSubmitted={() => {
            setPendingEdit(null);
            setToast({ message: "Opt-out updated.", type: "success" });
            triggerReload();
          }}
        />
      )}

      {importPreview && (
        <ImportPreviewModal
          csv={importPreview.csv}
          preview={importPreview.preview}
          onClose={() => setImportPreview(null)}
          onConfirmed={result => {
            setImportPreview(null);
            const parts = [
              `Imported ${result.imported}`,
              `collapsed ${result.collapsed}`,
              `skipped ${result.skipped}`,
            ];
            if (result.errors.length > 0) parts.push(`${result.errors.length} error(s)`);
            setToast({
              message: parts.join(", "),
              type: result.errors.length > 0 ? "warning" : "success",
            });
            triggerReload();
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmationModal
          title="Delete opt-out"
          message={`Remove the opt-out entry for ${pendingDelete.email}?`}
          warningMessage="This will allow the referee to be contacted again for this scope."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setPendingDelete(null)}
          onConfirm={handleDelete}
          backdropClick={false}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 6000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default OptOutsPage;
